import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IFuelOpsLossRow, ILossCaseModalProps } from 'types/fuelOps.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { formatNumbersForDisplay } from 'utils/helpers'
import {
  formatGhs,
  fuelLossCostGhs,
  FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
} from '../Shared/fuelFormatters'
import {
  LossApprovalBanner,
  LossCaseGrid,
  LossCaseItem,
  LossCaseValueEm,
  LossClosureWrap,
} from './LossCaseModal.styled'

export const CASE_STATUS_META: {
  [k in NonNullable<IFuelOpsLossRow['caseStatus']>]: { tone: IStatusPill['status']; label: string }
} = {
  OPEN: { tone: 'warning', label: 'Open' },
  PENDING_APPROVAL: { tone: 'accent', label: 'Pending approval' },
  CLOSED: { tone: 'neutral', label: 'Closed' },
}

export const OUTCOME_META: {
  [k in NonNullable<Exclude<IFuelOpsLossRow['outcome'], null>>]: {
    tone: IStatusPill['status']
    label: string
  }
} = {
  RECOVERED: { tone: 'success', label: 'Recovered' },
  WRITTEN_OFF: { tone: 'neutral', label: 'Written off' },
  DISCIPLINARY: { tone: 'danger', label: 'Disciplinary' },
}

export const LossCaseModal = ({
  loss,
  closeModal,
  onApproveWriteOff,
  onCloseWrittenOff,
  onRequestInvestigation,
}: ILossCaseModalProps) => {
  useModalClose(closeModal)
  const caseStatus = loss.caseStatus || 'OPEN'
  const costGhs = loss.costGhs ?? fuelLossCostGhs(loss.liters)
  const threshold = loss.approval?.requiredAboveGhs ?? FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS
  const recovered = loss.outcome === 'RECOVERED'

  return (
    <Modal black>
      <Flex column gap='0.5rem' width='100%'>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
          <TitleText weight='bold'>{`Loss case — ${loss.dateLabel} · ${loss.tank}`}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex center gap='0.5rem' wrap>
          <StatusPill
            status={loss.severity === 'critical' ? 'danger' : 'warning'}
            dot={loss.severity === 'critical'}
          >
            {loss.severity === 'critical' ? 'Critical' : 'Warning'}
          </StatusPill>
          <StatusPill status={CASE_STATUS_META[caseStatus].tone}>
            {CASE_STATUS_META[caseStatus].label}
          </StatusPill>
          {loss.outcome && (
            <StatusPill status={OUTCOME_META[loss.outcome].tone}>
              {OUTCOME_META[loss.outcome].label}
            </StatusPill>
          )}
        </Flex>
        <LossCaseGrid>
          <LossCaseItem>
            <OtherText grey>OPENED</OtherText>
            <LossCaseValueEm>{loss.dateLabel}</LossCaseValueEm>
          </LossCaseItem>
          <LossCaseItem>
            <OtherText grey>AMOUNT</OtherText>
            <LossCaseValueEm>{`${formatNumbersForDisplay(loss.liters)} L`}</LossCaseValueEm>
          </LossCaseItem>
          <LossCaseItem>
            <OtherText grey>COST AT WEIGHTED AVERAGE</OtherText>
            <LossCaseValueEm $color={recovered ? tokens.status.success.fg : undefined}>
              {recovered ? 'Recovered — no cost' : formatGhs(costGhs)}
            </LossCaseValueEm>
          </LossCaseItem>
          <LossCaseItem>
            <OtherText grey>OWNER</OtherText>
            <LossCaseValueEm>{loss.owner}</LossCaseValueEm>
          </LossCaseItem>
          <LossCaseItem>
            <OtherText grey>CLASSIFICATION</OtherText>
            <LossCaseValueEm>{loss.classification}</LossCaseValueEm>
          </LossCaseItem>
          <LossCaseItem>
            <OtherText grey>INVESTIGATION</OtherText>
            <LossCaseValueEm>{loss.status}</LossCaseValueEm>
          </LossCaseItem>
        </LossCaseGrid>
        {loss.note && <OtherText color={tokens.text.muted}>{loss.note}</OtherText>}
        <LossClosureWrap>
          <OtherText grey>CLOSURE</OtherText>
          {caseStatus === 'CLOSED' && (
            <>
              <BodyText>
                {`Closed ${loss.closedAt || '—'} — outcome ${loss.outcome ? OUTCOME_META[loss.outcome].label.toLowerCase() : '—'}`}
              </BodyText>
              {loss.approval?.approvedBy && (
                <OtherText color={tokens.status.success.fg}>
                  {`Write-off approved by ${loss.approval.approvedBy} · ${loss.approval.approvedAt || '—'}`}
                </OtherText>
              )}
            </>
          )}
          {caseStatus === 'PENDING_APPROVAL' && (
            <LossApprovalBanner>
              <BodyText color={tokens.status.warning.fg}>
                {loss.approval?.approvedBy
                  ? `Write-off approved by ${loss.approval.approvedBy} — confirm closure to write off ${formatGhs(costGhs)}`
                  : `Write-off ${formatGhs(costGhs)} exceeds ${formatGhs(threshold)} — requires ${loss.approval?.approverRole || 'GM'} approval`}
              </BodyText>
              <Flex gap='0.5rem'>
                {!loss.approval?.approvedBy && (
                  <Button variant='danger' size='sm' onClick={() => onApproveWriteOff(loss)}>
                    Approve write-off (GM)
                  </Button>
                )}
                {loss.approval?.approvedBy && onCloseWrittenOff && (
                  <Button variant='danger' size='sm' onClick={() => onCloseWrittenOff(loss)}>
                    Confirm write-off
                  </Button>
                )}
                <Button variant='secondary' size='sm' onClick={() => onRequestInvestigation(loss)}>
                  Request more investigation
                </Button>
              </Flex>
            </LossApprovalBanner>
          )}
          {caseStatus === 'OPEN' && (
            <OtherText color={tokens.text.muted}>
              Not closed — record an outcome (recovered, written off or disciplinary) when the
              investigation concludes.
            </OtherText>
          )}
        </LossClosureWrap>
      </Flex>
    </Modal>
  )
}
